import { Icon } from '@dynamic/ui';
import styled, { css } from 'styled-components';

import { LinkButton } from '#/components/LinkButton';
import { Markdown } from '#/components/Markdown';
import { hoverOpacity } from '#/components/styles';
import { remify } from '#/service/utils';

// wrapper for putting multiple TextBoxText children into a TextBox
export const TextBoxTexts = styled.div`
  display: flex;
  flex-direction: column;
  grid-area: text;
`;

TextBoxTexts.displayName = 'TextBoxTexts';

export const Title = styled.h1`
  font: var(--fontStyleTitle1);
  text-align: center;
  width: 100%;
  padding: 0 0 ${remify(14)} 0;
  color: var(--pageHeadingForeground);
  margin: 0;
`;

Title.displayName = 'Title';

export const SettingsContent = styled.div`
  width: 100%;
  max-width: ${remify(845)};
  padding: ${remify(60)} ${remify(30)};
`;
SettingsContent.displayName = 'SettingsContent';

export const Container = styled.div`
  display: flex;
  justify-content: center;
  padding-top: ${remify(80)};
  margin-bottom: ${remify(50)};
`;

Container.displayName = 'Container';

export const ItemHeader = styled.div`
  font: var(--fontStyleTitle3);
  color: var(--pageHeadingForeground);
  padding: ${remify(50)} 0 ${remify(6)};
`;

ItemHeader.displayName = 'ItemHeader';

export const MarginlessMarkdown = styled(Markdown)`
  p {
    margin: 0;
  }
`;

MarginlessMarkdown.displayName = 'MarginlessMarkdown';

type StyledTextBoxProps = {
  icon?: string;
  $hasItems?: boolean;
};

type StyledIconProps = {
  location: string;
};

export const textBoxBaseStyle = (icon?: string, isTransparent?: boolean) => css`
  position: relative;
  display: grid;
  grid-template:
    'title button'
    'text  button'
    / 1fr 0fr;
  min-height: ${remify(56)};
  padding: ${remify(13)} ${remify(24)};
  padding-left: ${icon ? remify(56) : remify(24)};
  background-color: ${isTransparent
    ? 'transparent'
    : 'var(--pageSecondaryBackground)'};
  border-radius: ${remify(4)};
  box-sizing: border-box;
  margin-bottom: ${remify(5)};
  place-items: center start;
  align-content: center;
`;

export const StyledTextBox = styled.div<StyledTextBoxProps>`
  ${({ icon, $hasItems }) => textBoxBaseStyle(icon, $hasItems)}
`;

export const BasicIcon = styled.img`
  height: ${remify(24)};
  margin-right: ${remify(8)};
  margin-left: ${remify(8)};
`;

BasicIcon.displayName = 'BasicIcon';

export const StyledTextBoxButton = styled.button<StyledTextBoxProps>`
  border: none;
  ${({ icon }) => textBoxBaseStyle(icon, true)}
  color: var(--pageParagraphForeground);
  width: auto;
  cursor: pointer;

  ${hoverOpacity}
`;

StyledTextBoxButton.displayName = 'StyledTextBoxButton';

export const TextBoxButton = styled(LinkButton)`
  margin-left: ${remify(24)};
  padding: 0 0.5rem;
  grid-area: button;
  white-space: nowrap;
`;

TextBoxButton.displayName = 'TextBoxButton';

export const StyledIcon = styled(Icon)<StyledIconProps>`
  position: absolute;
  top: ${remify(13)};
  ${({ location }) => location && `${location}: ${remify(16)};`}
  cursor: pointer;
`;
export const TextBoxTitle = styled.h3<{ $isOnlyText?: boolean }>`
  font: ${({ $isOnlyText }) =>
    $isOnlyText ? 'var(--fontStyleBody)' : 'var(--fontStyleSubtitle)'};
  margin: 0;
  text-align: left;
  grid-area: title;
  opacity: ${({ $isOnlyText }) => ($isOnlyText ? 1 : 0.7)};
`;

TextBoxTitle.displayName = 'TextBoxTitle';

export const TextBoxText = styled.div`
  font: var(--fontStyleBody);
  margin: 0;
  grid-area: text;

  /* sometimes we have to break long words, like an email address */
  overflow-wrap: anywhere;
`;

TextBoxText.displayName = 'TextBoxText';
